// ========================
// objectives.ts
// ========================
import { Contacts_Weighted_Scores_Args } from '@zaplify/graphql';
import { ContactClassificationMetadata } from '../schemas/contact-classification-metadata.schema';

// Define mappings from semantic names to GraphQL parameter names with validation
const objectiveWeightMapping = {
    networking: 'networking_objective_weight',
    sales: 'sales_objective_weight',
    customerSuccess: 'customer_success_objective_weight',
    fundraising: 'fundraising_objective_weight',
    recruiting: 'recruiting_objective_weight',
    internal: 'internal_objective_weight',
} as const satisfies Record<string, keyof Contacts_Weighted_Scores_Args>;

const temporalWeightMapping = {
    timing_urgency: 'timing_urgency_max_boost',
    urgent_followup_boost: 'urgent_followup_boost',
    no_action_penalty: 'no_action_penalty',
    shelf_base_boost: 'shelf_base_boost',
    shelf_period_days: 'shelf_period_days',
    very_old_overdue_base_boost: 'very_old_overdue_base_boost',
    very_old_decay_rate: 'very_old_decay_rate',
    due_today_boost: 'due_today_boost',
    due_curve_width: 'due_curve_width',
    future_penalty: 'future_penalty',
    future_decay_rate: 'future_decay_rate',
} as const satisfies Record<string, keyof Contacts_Weighted_Scores_Args>;

// Export the validated key types (derived from the validated mappings)
export type WeightKeys = keyof typeof objectiveWeightMapping;
export type TemporalWeightKeys = keyof typeof temporalWeightMapping;

export type UserObjectives = Partial<Record<WeightKeys | 'urgent', number>>;
export type UserTemporalWeights = Partial<Record<TemporalWeightKeys, number>>;

// 1.A Default weights: if nothing else is set, all are equal
export const DEFAULT_OBJECTIVE_WEIGHTS: Record<WeightKeys, number> = {
    networking: 1,
    sales: 1,
    customerSuccess: 1,
    fundraising: 1,
    recruiting: 1,
    internal: 1,
};

// Default temporal weights for timing behavior
export const DEFAULT_TEMPORAL_WEIGHTS: Record<TemporalWeightKeys, number> = {
    timing_urgency: 0.5,
    urgent_followup_boost: 1,
    no_action_penalty: 0.05,
    shelf_base_boost: 1.0,
    shelf_period_days: 14.0,
    very_old_overdue_base_boost: 0.9,
    very_old_decay_rate: 3.0,
    due_today_boost: 0.8,
    due_curve_width: 1.5,
    future_penalty: 0.984,
    future_decay_rate: 0.5,
};

export const DEFAULT_FILTER_BOOSTS: Record<WeightKeys | 'urgent', UserObjectives> = {
    urgent: {
        urgent: 1,
        recruiting: 1,
        networking: 1,
        internal: 1,
        sales: 1,
        customerSuccess: 1,
        fundraising: 1,
    },
    networking: {
        networking: 2,
        sales: 0,
        internal: -1,
        recruiting: 0,
        customerSuccess: 0,
        fundraising: 0,
        urgent: 0,
    },
    sales: {
        sales: 2,
        networking: 0,
        internal: -1,
        recruiting: 0,
        customerSuccess: 0,
        fundraising: 0,
        urgent: 0,
    },
    customerSuccess: {
        customerSuccess: 2,
        sales: 0,
        internal: -1,
        recruiting: 0,
        networking: 0,
        fundraising: 0,
        urgent: 0,
    },
    fundraising: {
        fundraising: 1,
        networking: 0,
        internal: 0,
        recruiting: 0,
        sales: 0,
        customerSuccess: 0,
        urgent: 0,
    },
    recruiting: {
        recruiting: 1,
        networking: 0,
        internal: 0,
        sales: 0,
        customerSuccess: 0,
        fundraising: 0,
        urgent: 0,
    },
    internal: {
        internal: 1,
        customerSuccess: 0,
        sales: 0,
        networking: 0,
        recruiting: 0,
        fundraising: 0,
        urgent: 0,
    },
};

export const getUserInteractionWeights = (contactMetadata: ContactClassificationMetadata, factor: number) => {
    const weights = {
        objectiveScores: {
            networking: 0,
            sales: 0,
            customerSuccess: 0,
            fundraising: 0,
            recruiting: 0,
            internal: 0,
        },
    } as ContactClassificationMetadata;
    if (contactMetadata) {
        weights.objectiveScores = Object.fromEntries(
            Object.entries(contactMetadata.objectiveScores).map(([key, value]) => {
                if (value <= 40) {
                    return [key, 0];
                }
                const newValue = Number(((value / 100) * factor).toFixed(3));
                return [key, newValue];
            }),
        ) as ContactClassificationMetadata['objectiveScores'];
    }
    return weights;
};

/**
 * mergeWeights(...sources)
 *
 * Takes any number of { [objectiveKey]: weight } objects,
 * in priority order, and merges them into one bag.  Later sources
 * override earlier ones.  We always start from the DEFAULT_OBJECTIVE_WEIGHTS.
 */
export function mergeWeights(...sources: Array<UserObjectives | null | undefined>): Record<WeightKeys, number> {
    // Start with a shallow copy of the defaults
    const result: Record<WeightKeys, number> = { ...DEFAULT_OBJECTIVE_WEIGHTS };

    // For each source in order, copy over any keys it defines
    for (const src of sources) {
        if (!src) continue;
        for (const [k, v] of Object.entries(src)) {
            if (v == null) continue;
            result[k as WeightKeys] = v;
        }
    }

    return result;
}

/**
 * mergeTemporalWeights(...sources)
 *
 * Takes any number of { [temporalKey]: weight } objects,
 * in priority order, and merges them into one bag.  Later sources
 * override earlier ones.  We always start from the DEFAULT_TEMPORAL_WEIGHTS.
 */
export function mergeTemporalWeights(
    ...sources: Array<UserTemporalWeights | null | undefined>
): Record<TemporalWeightKeys, number> {
    // Start with a shallow copy of the defaults
    const result: Record<TemporalWeightKeys, number> = { ...DEFAULT_TEMPORAL_WEIGHTS };

    // For each source in order, copy over any keys it defines
    for (const src of sources) {
        if (!src) continue;
        for (const [k, v] of Object.entries(src)) {
            if (v == null) continue;
            result[k as TemporalWeightKeys] = v;
        }
    }

    return result;
}

// Transform weights to match GraphQL query parameter names using the validated mappings
export const transformWeightsForQuery = (
    objectives: Record<WeightKeys, number>,
    temporal?: Record<TemporalWeightKeys, number>,
): Contacts_Weighted_Scores_Args => {
    const temporalWeights = temporal || DEFAULT_TEMPORAL_WEIGHTS;

    // Use the mappings to ensure type safety
    const result: Partial<Contacts_Weighted_Scores_Args> = {};

    // Map objective weights
    for (const [semanticKey, graphqlKey] of Object.entries(objectiveWeightMapping)) {
        result[graphqlKey] = objectives[semanticKey as WeightKeys];
    }

    // Map temporal weights
    for (const [semanticKey, graphqlKey] of Object.entries(temporalWeightMapping)) {
        result[graphqlKey] = temporalWeights[semanticKey as TemporalWeightKeys];
    }

    return result as Contacts_Weighted_Scores_Args;
};

/**
 * Boosts weights from GraphQL query variables based on a factor, but only for values
 * that differ from the defaults.
 *
 * @param gqlQueryVariables - The GraphQL query variables containing weights
 * @param factor - The factor to boost by (positive for thumbs up, negative for thumbs down)
 * @returns An object with objectiveScores and temporalScores
 */
export const boostWeightsFromGqlVariables = (
    gqlQueryVariables: Partial<Contacts_Weighted_Scores_Args> | undefined,
    factor: number,
) => {
    if (!gqlQueryVariables) {
        return {
            objectiveScores: {},
            temporalScores: {},
        };
    }

    // Create reverse mappings for easier lookup
    const reverseObjectiveMapping = Object.entries(objectiveWeightMapping).reduce((acc, [semanticKey, graphqlKey]) => {
        acc[graphqlKey] = semanticKey as WeightKeys;
        return acc;
    }, {} as Record<keyof Contacts_Weighted_Scores_Args, WeightKeys>);

    const reverseTemporalMapping = Object.entries(temporalWeightMapping).reduce((acc, [semanticKey, graphqlKey]) => {
        acc[graphqlKey] = semanticKey as TemporalWeightKeys;
        return acc;
    }, {} as Record<keyof Contacts_Weighted_Scores_Args, TemporalWeightKeys>);

    // Process objective weights
    const objectiveScores: Record<string, number> = {};
    Object.entries(gqlQueryVariables).forEach(([graphqlKey, value]) => {
        const semanticKey = reverseObjectiveMapping[graphqlKey as keyof Contacts_Weighted_Scores_Args];
        if (semanticKey && value !== undefined) {
            // If different from default, apply factor; otherwise set to 0
            if (value !== DEFAULT_OBJECTIVE_WEIGHTS[semanticKey]) {
                objectiveScores[semanticKey] = value * factor;
            } else {
                objectiveScores[semanticKey] = 0;
            }
        }
    });

    // Process temporal weights
    const temporalScores: Record<string, number> = {};
    Object.entries(gqlQueryVariables).forEach(([graphqlKey, value]) => {
        const semanticKey = reverseTemporalMapping[graphqlKey as keyof Contacts_Weighted_Scores_Args];
        if (semanticKey && value !== undefined) {
            // If different from default, apply factor; otherwise set to 0
            if (value !== DEFAULT_TEMPORAL_WEIGHTS[semanticKey]) {
                temporalScores[semanticKey] = value * factor;
            } else {
                temporalScores[semanticKey] = 0;
            }
        }
    });

    return {
        objectiveScores,
        temporalScores,
    };
};
